import { useRef } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { CheckIcon, ChevronDownIcon } from '@xantory/icons';
import { useChromePopover } from '../../hooks/use-chrome-popover';
import { styles } from './site-switcher.styles';
export interface SiteOption {
  id: string;
  name: string | null;
  /** Pre-derived dot tone (see `StatusTone`). */
  tone?: StatusTone;
  /** Pre-derived, already-translated health word — shown in brackets after the name. */
  statusLabel?: string;
}

/** The dot colour, already decided by the caller.
 *
 *  **Deliberately a TONE, not a status.** This component used to take a health
 *  enum and map it to a colour itself, which made it a second opinion on what a
 *  status means — and it drifted from `/admin/sites`, so the same site showed
 *  different words in two places. The mapping now lives in exactly one place
 *  (`features/src/shared/site-status.ts`) and this component just paints. */
export type StatusTone = 'ok' | 'warn' | 'danger' | 'muted';
interface SiteSwitcherProps {
  /** Current site display name. */
  name: string;
  /** Selectable sites for the dropdown. */
  sites: SiteOption[];
  currentId?: string | null;
  /** Dot tone for the selected site, already derived by the caller. */
  tone?: StatusTone;
  onSelect: (site: SiteOption) => void;
}
const DOT_STYLE = {
  ok: styles.dotHealthy,
  warn: styles.dotWarning,
  danger: styles.dotCritical,
  muted: styles.dotStale
} as const;
export function SiteSwitcher({
  name,
  sites,
  currentId,
  tone = 'muted',
  onSelect
}: SiteSwitcherProps) {
  // §24 — hidden scrollbar; the panel scrolls on a phone.
  const menuProps = stylex.props(styles.menu);
  const btnRef = useRef<HTMLButtonElement>(null);
  // Shared with every other top-bar dropdown, so opening one closes the rest —
  // and close-on-outside-click / Esc come with it.
  const {
    open,
    close,
    onTriggerClick
  } = useChromePopover(btnRef);
  return <div
  // Synchronous Esc path — the window listener attaches a tick after
  // render, so an immediate Esc must be caught here.
  onKeyDown={e => {
    if (e.key === 'Escape' && open) {
      close();
      btnRef.current?.focus();
    }
  }} {...stylex.props(styles.wrap)}>
      <button ref={btnRef} type="button" aria-haspopup="menu" aria-expanded={open} onClick={onTriggerClick} {...stylex.props(styles.btn)}>
        <span {...stylex.props(styles.dot, DOT_STYLE[tone])} />
        <span {...stylex.props(styles.name)}>{name}</span>
        <ChevronDownIcon size={15} {...stylex.props(styles.chev, open && styles.chevOpen)} />
      </button>

      {open && <div onClick={e => e.stopPropagation()} {...menuProps} className={[menuProps.className, 'scroll-hidden'].filter(Boolean).join(' ')}>
          {sites.map(s => {
        const active = s.id === currentId;
        return <button key={s.id} type="button" onClick={() => {
          onSelect(s);
          close();
        }} {...stylex.props(styles.item, active && styles.itemOn)}>
                <span {...stylex.props(styles.itemDot, DOT_STYLE[s.tone ?? 'muted'])} />
                <span {...stylex.props(styles.itemName)}>
                  {s.name ?? s.id}
                  {s.statusLabel != null && <span {...stylex.props(styles.itemStatus)}> ({s.statusLabel})</span>}
                </span>
                {active && <CheckIcon size={14} strokeWidth={2.5} {...stylex.props(styles.check)} />}
              </button>;
      })}
        </div>}
    </div>;
}